import React, { type ReactNode } from 'react';
import ToastContextProvider from '@lib/components/toast-system/toast-context';
import ToastManager from '@lib/components/toast-system/toast-manager';
import { createToastStore } from '@lib/components/toast-system/toast-store';

import ToastRenderer from './toast-renderer-default';
import type { ToastDefinition } from './toast-system-default.types';

import styles from './styles.scss';

// This component is a convenience wrapper around everything needed to
// setup toasts in the app or for individual webview routes.
// If you need to customize the toast system, you could essentially
// copy this component and make the necessary changes or leverage
// the individual components directly. This component illustrates
// the various pieces needed to make the toast system work:
// - A toast store
// - A toast manager
// - A toast renderer
// - A toast context provider

// The `ToastSystemDefault` component is only expected to be declared once
// in the component tree. Therefore, we store the toast store in a module-level
// variable to ensure that the same store is used across potential re-renders.
let toastStoreInstance: ReturnType<typeof createToastStore<ToastDefinition>> | undefined;

export interface ToastSystemDefaultProps {
  children: ReactNode;
  /**
   * When provided, this store will be used for the toast system.
   * If not provided, a store will be created internally.
   * This prop is primarily used for testing/storybook purposes.
   */
  toastStore?: ReturnType<typeof createToastStore<ToastDefinition>>;
}

const ToastSystemDefault = ({
  children,
  toastStore,
}: {
  children: ReactNode;
  /**  */
  toastStore?: ReturnType<typeof createToastStore<ToastDefinition>>;
}) => {
  toastStoreInstance = toastStore ?? createToastStore<ToastDefinition>();

  return (
    <>
      <ToastManager
        classNames={{
          animateIn: styles.animateIn,
          animateOut: styles.animateOut,
          container: styles.toastContainer,
        }}
        ToastRenderer={ToastRenderer}
        toastStore={toastStoreInstance}
      />
      <ToastContextProvider toastStore={toastStoreInstance}>{children}</ToastContextProvider>
    </>
  );
};

export default ToastSystemDefault;
